import Konva from 'konva';
import * as jsts from 'jsts';

import { Element } from '../../equipment/models/elements/element';
import { EquipmentElements } from '../../equipment/models/elements/equipmentElements';
import { HookElement } from './elements/hookElement';
import { IPlanogram } from './planogram';
import { konvaSettings } from '../../equipment/models/konvaSettings';
import { PlanogramViewBehavior } from './planogramViewBehavior';
import { Point } from '../../core/models/point';
import { POSMaterial } from './POSMaterials/POSMaterial';
import { ProductGroup } from './product-group';

import { Utils } from '../../core/services/utils.service';
import { Bounding } from '../../core/models/bounding';

const COORDINATE = jsts.geom.Coordinate;
const GEOMETRY = new jsts.geom.GeometryFactory();
const PLANE_ELEMENTS: Array<EquipmentElements> = [
	EquipmentElements.BaseRectangle,
	EquipmentElements.BaseTrapeze,
	EquipmentElements.Shelf,
	EquipmentElements.AngleShelf
]

export class PlanogramFrontBehavior extends PlanogramViewBehavior {

	constructor(planogram: IPlanogram, detailedElement: Element, scale: number, beginPoint: Point) {
		super(planogram, detailedElement, scale, beginPoint);
	}

	public convertCanvasPointToPlanogram(point: Point): Point {
		const canvasXPoint: number = point.x - konvaSettings.canvasOffset - this.beginPoint.x;
		const canvasYPoint: number = point.y - konvaSettings.canvasOffset - this.beginPoint.y;

		return {
			x: Utils.round(canvasXPoint / this.scale),
			y: Utils.round(this.planogram.stand.Height - canvasYPoint / this.scale),
			z: this.planogram.stand.Depth
		};
	}

	public convertPlanogramPointToCanvas(point: Point): Point {
		return {
			x: Utils.round(point.x * this.scale + konvaSettings.canvasOffset + this.beginPoint.x),
			y: Utils.round((this.planogram.stand.Height - point.y) * this.scale + konvaSettings.canvasOffset + this.beginPoint.y)
		};
	}

	public getActiveProductGroups(activeProducts: Array<ProductGroup>): Array<Array<ProductGroup>> {
		const activeProductGroups: Array<Array<ProductGroup>> = Utils.groupBy<ProductGroup>(
			activeProducts.sort((a: ProductGroup, b: ProductGroup) => a.begin.x - b.begin.x),
			'element'
		)
			.reduce((result: Array<Array<ProductGroup>>, group: Array<ProductGroup>) => {
				if (group[0].element.is(HookElement)) {
					return [...result, group];
				} else {
					return [
						...result,
						...Utils.groupByChildProperty(group, 'begin', 'x')
					];
				}
			}, [])
			.sort((a: Array<ProductGroup>, b: Array<ProductGroup>) => a[0].begin.x - b[0].begin.x);

		activeProductGroups.forEach((group: Array<ProductGroup>) => {
			group.sort((a: ProductGroup, b: ProductGroup) => b.begin.z - a.begin.z || a.begin.y - b.begin.y);
		});

		return activeProductGroups;
	}

	public getAnchorElement(product: ProductGroup, point: Point): Element {
		let element: Element = this.findHangElement(product, point);
		if (!element) element = this.findPlaneElement(point);

		return element;
	}

	public getCorrectZCoordinate(element: Element, z: number, product: ProductGroup): number {
        const beginDelta: number = Utils.round(product.getBoundingZ().max - product.begin.z);

		return Utils.round(element.getAbsoluteBoundingZ().max - beginDelta);
	}

	public getOffset(): Point {
		return { x: 0, y: 0 };
	}

	public getPoint(product: ProductGroup, canvasElement: Konva.Group): Point {
		const canvasXPoint: number = canvasElement.x() - konvaSettings.canvasOffset - this.beginPoint.x;
		const canvasYPoint: number = canvasElement.y() + canvasElement.height() - konvaSettings.canvasOffset - this.beginPoint.y;
		const deltaX: number = Utils.round(product.getBoundingX().min - product.begin.x);

		return {
			x: Utils.round(canvasXPoint / this.scale - deltaX),
			y: Utils.round(this.planogram.stand.Height - canvasYPoint / this.scale),
			z: this.planogram.stand.Depth
		};
	}

	public getProductAngle(element: Element, point: Point): number {
		return 0;
	}

	public moveProducts(productGroups: Array<ProductGroup>, horizontal: number, vertical: number): boolean {
		if (!horizontal || !productGroups.length) return false;

		let movementPerformed: boolean = false;
		productGroups.forEach((pg: ProductGroup) => {
			if (pg.canMove(horizontal)) {
				pg.move(horizontal);
				movementPerformed = true;
			}
		});

		return movementPerformed;
	}

	public movePOSMaterials(materials: Array<POSMaterial>, horizontal: number, vertical: number): boolean {
		if ((!horizontal && !vertical) || !materials.length) return false;

		let movementPerformed: boolean = false;
		materials.forEach((m: POSMaterial) => {
			m.x = Utils.round(m.x + horizontal);
			m.y = Utils.round(m.y + vertical);
			movementPerformed = true;
		});

		return movementPerformed;
	}

	private findHangElement(product: ProductGroup, point: Point): Element {
		const deltaX: number = product.begin.x - point.x;
		const boundingX: Bounding = product.getBoundingX();
		const productPoints: Array<Point> = [
			{ x: boundingX.min - deltaX, y: point.y },
			{ x: boundingX.max - deltaX, y: point.y },
			{ x: boundingX.max - deltaX, y: point.y + product.Height },
			{ x: boundingX.min - deltaX, y: point.y + product.Height },
			{ x: boundingX.min - deltaX, y: point.y }
		];
		const productPolygon: jsts.geom.Polygon = GEOMETRY.createPolygon(
			productPoints.map((p: Point) => new COORDINATE(p.x, p.y))
		);
		const hangElements: Array<Element> = this.planogram.stand.allElements
			.filter((el: Element) => ['HoleElement', 'HookElement', 'HangBarElement'].includes(el.constructor.name))
			.filter((el: Element) => {
				const elementPolygon: jsts.geom.Polygon = GEOMETRY.createPolygon(
					el.shapes[0].coordinates.map((p: Point) => new COORDINATE(p.x, p.y))
				);

				return productPolygon.intersects(elementPolygon);
			})
			.sort((a: Element, b: Element) => {
				const productPoint: Point = {
					x: product.element.stand ? point.x + product.width / 2 : point.x,
					y: product.element.stand ? point.y + product.Height : point.y
				}; // TODO replace with singleton NullElement
				const distanceA: number = Utils.distance(
					a.shapes[0].begin.x,
					a.shapes[0].begin.y,
					productPoint.x,
					productPoint.y
				);
				const distanceB: number = Utils.distance(
					b.shapes[0].begin.x,
					b.shapes[0].begin.y,
					productPoint.x,
					productPoint.y
				);

				return distanceA - distanceB;
			});

		return hangElements[0];
	}

	private findPlaneElement(point: Point): Element {
		const planeElement: Element = this.planogram.stand.allElements
			.filter((el: Element) => el.parent.is(...PLANE_ELEMENTS))
			.filter((el: Element) => el.shapes[0].isPointOn(point.x))
			.sort((a: Element, b: Element) => b.getBoundingY().min - a.getBoundingY().min)
			.find((el: Element) => el.getBoundingY().min < point.y);

		if (!planeElement) return this.planogram.stand.bases.find(base => base.shapes[0].isPointOn(point.x)) || null;

		return planeElement;
	}
}